import type { TraceScene } from '../core/TraceScene';

export interface SettleOptions {
    /**
     * Upper bound on the total wait. A scene caught in a pathological
     * re-invalidation loop never goes quiet; it must still export — a
     * slightly stale arrangement beats a hang.
     * @default 2000
     */
    maxWaitMs?: number;
    /**
     * Consecutive macrotask turns that must pass without a layout solve
     * before the scene counts as settled. Without the solve-notification
     * hook this is the fixed number of drain turns instead.
     * @default 4
     */
    quietTurns?: number;
}

const DEFAULT_MAX_WAIT_MS = 2000;
const DEFAULT_QUIET_TURNS = 4;

/**
 * Structural probe for the layout solve-notification hook that lands with
 * PR #681. Declared locally rather than referencing TraceScene's type so
 * this module compiles today — and because plain property access performs
 * the detection, the event-driven path below activates automatically when
 * #681 merges, with zero changes here. If #681 ships the hook with a
 * different shape, the assignment in awaitLayoutQuiescence stops compiling,
 * which is the drift alarm we want.
 */
interface LayoutSolveNotifier {
    /** Subscribe to layout-solve completions; returns an unsubscribe. */
    onLayoutSolved(listener: () => void): () => void;
}

/** One macrotask turn — see awaitLayoutQuiescence for why macrotasks. */
function macrotaskTurn(): Promise<void> {
    return new Promise<void>((resolve) => { setTimeout(resolve, 0); });
}

/**
 * Wait for layout to stop moving before an export captures transforms
 * (issue #664: an export taken mid-load freezes a pre-final arrangement).
 *
 * Layout solves are microtask-batched (Layout._scheduleBatch): every
 * invalidation coalesces into one queueMicrotask'd pass, and a pass can
 * re-invalidate (size receivers, two-phase loads), chaining further passes.
 * Waiting MACROTASK turns is what makes the drain effective: each turn runs
 * a full microtask checkpoint — the pending batch plus anything it chains —
 * and gives invalidations that arrive on timers or I/O (asset loads) a turn
 * boundary to land on before quiet is declared.
 *
 * Deliberately NOT scene.whenReadyAsync(): PBRMaterial.isReady(mesh) can
 * stay false across render passes, so that call hangs indefinitely on
 * PBR-heavy scenes (see the note on TraceScene.frameScene). Shader/texture
 * readiness is irrelevant to serialization anyway — only transforms matter
 * here.
 *
 * Two paths, chosen by feature detection:
 * - `onLayoutSolved` present (arrives with PR #681): subscribe, and resolve
 *   once `quietTurns` consecutive macrotask turns pass with no solve event.
 * - No hook: degrade to a fixed drain of `quietTurns` macrotask turns.
 *
 * `maxWaitMs` bounds pathological re-invalidation loops in both paths.
 */
export async function awaitLayoutQuiescence(
    traceScene: TraceScene,
    opts?: SettleOptions,
): Promise<void> {
    const maxWaitMs = opts?.maxWaitMs ?? DEFAULT_MAX_WAIT_MS;
    const quietTurns = opts?.quietTurns ?? DEFAULT_QUIET_TURNS;
    const deadline = Date.now() + maxWaitMs;

    // Intersection assertion, not `any`: the probe stays typed, and once
    // TraceScene itself declares onLayoutSolved a conflicting signature
    // makes the intersection's property `never` and the subscribe call
    // below stops compiling — see LayoutSolveNotifier.
    const notifier = traceScene as TraceScene & Partial<LayoutSolveNotifier>;

    if (typeof notifier.onLayoutSolved === 'function') {
        let solvedThisTurn = false;
        const unsubscribe = notifier.onLayoutSolved(() => { solvedThisTurn = true; });
        try {
            let quiet = 0;
            while (quiet < quietTurns) {
                if (Date.now() >= deadline) return;
                await macrotaskTurn();
                if (solvedThisTurn) {
                    solvedThisTurn = false;
                    quiet = 0;
                } else {
                    quiet++;
                }
            }
        } finally {
            unsubscribe();
        }
        return;
    }

    for (let turn = 0; turn < quietTurns; turn++) {
        if (Date.now() >= deadline) return;
        await macrotaskTurn();
    }
}
